// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import { Box, Checkbox, Collapse, FormControlLabel, IconButton, Switch, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTheme } from '@mui/system';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { ToggleFileFootprintsLayerAction, ToggleFootprintKindAction } from '../mapCommonActions';
import { kindLineColor, KIND_DASHES } from './footprintGeoJson';
import stylesDef from './styles';

// Same section names as the Data Sets panel, so the two surfaces speak one
// language about file kinds.
const KIND_LABELS: Record<FileSelectionKind, string> = {
  tif: 'Imagery', las: 'Point Clouds', pano: 'Panoramas', poleImage: 'Pole Images',
};

interface ComponentProps {
  kinds: FileSelectionKind[];
}

// The File Footprints switch, grown into a small layers card (D78): with 2+
// kinds on the pane a chevron (2D-pane convention: right = collapsed, down =
// expanded) opens a per-kind filter list. Rows exist only for kinds actually
// present; an unchecked kind leaves the MAP entirely while its sidebar rows
// keep working (D77b). Each row carries a legend swatch of the kind's actual
// outline style.
export const FootprintsSwitch: React.FC<ComponentProps> = ({ kinds }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const on = useAppSelector((state) => state.map.common.layers.fileFootprints);
  const hiddenKinds = useAppSelector((state) => state.map.common.hiddenFootprintKinds);
  const [expanded, setExpanded] = useState(false);

  // Switch off hides the list; coming back starts collapsed with every kind
  // re-checked (the slice resets the filter on the same toggle).
  useEffect(() => {
    if (!on) setExpanded(false);
  }, [on]);

  const listAvailable = on && kinds.length >= 2;

  return (
    <Box sx={styles.switchCard}>
      <Box sx={styles.switchRow}>
        <FormControlLabel
          sx={styles.switchControl}
          label={<Typography sx={styles.switchLabel}>File Footprints</Typography>}
          control={(
            <Switch
              size="small"
              color="primary"
              checked={on}
              onChange={() => dispatch(ToggleFileFootprintsLayerAction())}
            />
          )}
        />
        {listAvailable && (
          <IconButton
            size="small"
            aria-label={expanded ? 'Collapse footprint kinds' : 'Expand footprint kinds'}
            sx={styles.chevronButton}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded
              ? <KeyboardArrowDownIcon fontSize="small" />
              : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>
      <Collapse in={listAvailable && expanded}>
        <Box sx={styles.kindRows}>
          {kinds.map((kind) => (
            <Box key={kind} sx={styles.kindRow}>
              <FormControlLabel
                sx={styles.kindControl}
                label={<Typography sx={styles.switchLabel}>{KIND_LABELS[kind]}</Typography>}
                control={(
                  <Checkbox
                    size="small"
                    sx={styles.kindCheckbox}
                    checked={!hiddenKinds.includes(kind)}
                    onChange={() => dispatch(ToggleFootprintKindAction(kind))}
                  />
                )}
              />
              {/* Dashes scaled up with the heavier swatch stroke so the sample
                  reads like the map line, not a finer pattern. */}
              <svg width="26" height="4" aria-hidden="true">
                <line
                  x1="0"
                  y1="2"
                  x2="26"
                  y2="2"
                  stroke={kindLineColor(kind, theme.palette.secondary.main)}
                  strokeWidth="3"
                  strokeDasharray={KIND_DASHES[kind]?.map((d) => d * 2).join(' ')}
                />
              </svg>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default FootprintsSwitch;
